import React, {
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Node,
  Edge,
  useReactFlow,
  ReactFlowProvider,
  NodeMouseHandler,
} from '@xyflow/react';

import { v4 as uuidv4 } from 'uuid';

import '@xyflow/react/dist/style.css';
import './style.css';

import nodeTypes from './nodes/nodeTypes';
import {
  Breadcrumb,
  Button,
  Empty,
  Flex,
  message,
  Modal,
  Spin,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import ToolsPanel, { ToolType } from './ToolsPanel';
import { Link, useParams } from 'react-router-dom';
import {
  ExecutionResult,
  GraphListEntry,
  IOValuesType,
  NodeDescription,
  useClient,
} from '../client';
import { AxiosError, CanceledError } from 'axios';
import {
  CaretRightOutlined,
  ExportOutlined,
  ImportOutlined,
  RedoOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { IONodeType } from './nodes/IONode';
import { IfNodeType } from './nodes/IfNode';
import { SubgraphNodeType } from './nodes/SubgraphNode';
import { OperatorNodeType } from './nodes/OperatorNode';
import ErrorPlaceholder from './ErrorPlaceholder';
import TestPanel from './TestPanel';
import { AppContext } from '../context';
import { allDataTypes } from './nodes/types';

const initialNodes: Node[] = [
  {
    id: uuidv4(),
    position: { x: 300, y: 0 },
    data: {
      id: 0,
      isOutput: false,
      dataType: 'text',
      outputs: [{ data_types: ['text'] }],
    },
    type: 'IO',
  } as IONodeType,
  {
    id: uuidv4(),
    position: { x: 300, y: 400 },
    data: {
      id: 0,
      dataType: 'text',
      isOutput: true,
      inputs: [{ data_types: ['text'] }],
    },
    type: 'IO',
  } as IONodeType,
];
const initialEdges: Edge[] = [];

function createNode(
  toolType: ToolType,
  x: number,
  y: number,
  operatorNodesDescr: NodeDescription[]
): Node | null {
  switch (toolType) {
    case 'input':
      return {
        id: uuidv4(),
        position: { x, y },
        data: {
          id: 0,
          isOutput: false,
          dataType: 'text',
          outputs: [{ data_types: ['text'] }],
        },
        type: 'IO',
      } as IONodeType;
    case 'if':
      return {
        id: uuidv4(),
        position: { x, y },
        data: {},
        type: 'If',
      } as IfNodeType;
    case 'subgraph':
      return {
        id: uuidv4(),
        position: { x, y },
        data: {
          type: 'subgraph',
          graphs: [],
          inputs: [{ data_types: allDataTypes }],
          outputs: [{ data_types: allDataTypes }],
        },
        type: 'Subgraph',
      } as SubgraphNodeType;
    case 'foreach':
      return {
        id: uuidv4(),
        position: { x, y },
        data: {
          type: 'foreach',
          graphs: [],
          inputs: [{ data_types: allDataTypes }],
          outputs: [{ data_types: allDataTypes }],
        },
        type: 'Subgraph',
      } as SubgraphNodeType;
  }

  for (const nodeDescr of operatorNodesDescr) {
    if (nodeDescr.name !== toolType) {
      continue;
    }
    return {
      id: uuidv4(),
      position: { x, y },
      data: {
        operatorName: nodeDescr.name,
        inputs: nodeDescr.inputs,
        outputs: nodeDescr.outputs,
        label: nodeDescr.label,
        params: nodeDescr.parameters,
      },
      type: 'Operator',
    } as OperatorNodeType;
  }

  return null;
}

type ExecutionOutputsType = Map<string, Map<string | null, IOValuesType[]>>;

const getEdgeData = (executionOutputs: ExecutionOutputsType, edge: Edge) => {
  const nodeOutputs = executionOutputs.get(edge.source);
  if (nodeOutputs) {
    return nodeOutputs.get(edge.sourceHandle || null);
  }
};

type HistoryEntry = {
  nodes: Node[];
  edges: Edge[];
};

const HISTORY_SIZE = 20;
const HISTORY_INTERVAL = 1000; // ms

function GraphEditor() {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const history = useRef<HistoryEntry[]>([]);
  const historyIdx = useRef(-1);
  const lastHistoryTs = useRef(0);
  const whasHistoryChange = useRef(false);

  const [graphName, setGraphName] = useState('');
  const [graphs, setGraphs] = useState<GraphListEntry[]>([]);
  const [nodesDescriptions, setNodesDescriptions] = useState<NodeDescription[]>(
    []
  );

  const [loading, setLoading] = useState(false);
  const [errorFetching, setErrorFetching] = useState(false);

  const [changed, setChanged] = useState(false);
  const [saveRequested, setSaveRequested] = useState(false);
  const [saving, setSaving] = useState(false);

  const { screenToFlowPosition, updateNodeData } = useReactFlow();

  const [selectedTool, setSelectedTool] = useState<ToolType>('select');

  const { graphId = '' } = useParams();
  const { projectId, projectName } = useContext(AppContext);

  const [messageApi, contextHolder] = message.useMessage();

  const [testOpen, setTestOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const exportLinkRef = useRef<HTMLAnchorElement>(null);

  const [selectedObjectData, setSelectedObjectData] = useState<
    IOValuesType[] | null
  >(null);
  const [executionOutputs, setExecutionOutputs] =
    useState<ExecutionOutputsType>(new Map());
  const [lastExecutionResult, setLastExecutionResult] =
    useState<ExecutionResult | null>(null);

  const client = useClient(projectId);

  useEffect(() => {
    if (projectId === null) return;
    setLoading(true);
    (async () => {
      try {
        const graphData = await client.getGraph(graphId || '');
        const graphsList = await client.listGraphs();
        const operatorNodesDescriptions =
          await client.getOperatorNodesDescriptions();
        setGraphs(
          graphsList
            .map(({ id, name, ...rest }) => ({ id, name: name || id, ...rest }))
            .filter(({ id }) => id !== graphId)
        );
        setNodesDescriptions(operatorNodesDescriptions);
        setLoading(false);
        setGraphName(graphData.name || '');
        if (!graphData.graph) {
          setNodes(initialNodes);
          setEdges(initialEdges);
          return;
        }
        setNodes(graphData.graph.nodes);
        setEdges(graphData.graph.edges);
      } catch (error) {
        if (error instanceof CanceledError) return;
        setLoading(false);
        setErrorFetching(true);
        if (error instanceof AxiosError && error.status === 404) {
          messageApi.error('Graph not found');
        } else {
          messageApi.error('Error fetching graph');
          console.log('Error fetching graph:', error);
        }
      }
    })();
  }, [messageApi, projectId, graphId, setNodes, setEdges, client]);

  useEffect(() => {
    setChanged(true);

    if (whasHistoryChange.current) {
      whasHistoryChange.current = false;
      return;
    }

    if (
      !nodes.length ||
      Date.now() - lastHistoryTs.current < HISTORY_INTERVAL
    ) {
      return;
    }

    lastHistoryTs.current = Date.now();

    if (historyIdx.current < history.current.length - 1) {
      history.current = [
        ...history.current.slice(0, historyIdx.current + 1),
        { nodes, edges },
      ];
    } else if (history.current.length >= HISTORY_SIZE) {
      history.current = [
        ...history.current.slice(history.current.length - HISTORY_SIZE + 1),
        { nodes, edges },
      ];
    } else {
      history.current = [...history.current, { nodes, edges }];
    }
    historyIdx.current = Math.min(
      historyIdx.current + 1,
      history.current.length - 1
    );
  }, [nodes, edges]);

  useEffect(() => {
    if (loading || errorFetching) return;
    const saveTimeoutId = window.setInterval(() => {
      setSaveRequested(true);
    }, 5000);
    return () => {
      window.clearInterval(saveTimeoutId);
      console.log('Timeout cancelled');
    };
  }, [loading, errorFetching]);

  useEffect(() => {
    if (
      loading ||
      errorFetching ||
      !changed ||
      !saveRequested ||
      saving ||
      projectId === null
    ) {
      return;
    }
    setSaving(true);
    setChanged(false);
    setSaveRequested(false);

    (async () => {
      try {
        await client.updateGraph(
          graphId,
          {
            id: graphId,
            graph: { id: graphId, nodes, edges },
          },
          true
        );
      } catch (error) {
        if (error instanceof CanceledError) {
          console.log('save cancelled');
          return;
        }
        messageApi.error('Error saving graph');
        console.log('Error saving graph:', error);
      }
      setSaving(false);
    })();
  }, [
    loading,
    errorFetching,
    changed,
    saveRequested,
    saving,
    projectId,
    graphId,
    nodes,
    edges,
    messageApi,
    client,
    historyIdx,
  ]);

  const undo = useCallback(() => {
    historyIdx.current = Math.max(0, historyIdx.current - 1);
    const histEntry = history.current[historyIdx.current];
    setNodes(histEntry.nodes);
    setEdges(histEntry.edges);
    whasHistoryChange.current = true;
  }, [setEdges, setNodes]);

  const redo = useCallback(() => {
    if (historyIdx.current >= history.current.length - 1) {
      return;
    }
    historyIdx.current = Math.min(
      history.current.length - 1,
      historyIdx.current + 1
    );
    const histEntry = history.current[historyIdx.current];
    setNodes(histEntry.nodes);
    setEdges(histEntry.edges);
    whasHistoryChange.current = true;
  }, [setEdges, setNodes]);

  useEffect(() => {
    const onCtrlZ = (event: KeyboardEvent) => {
      if (event.key === 'z' && event.ctrlKey) {
        undo();
      } else if (event.key === 'Z' && event.ctrlKey) {
        redo();
      }
    };
    document.addEventListener('keydown', onCtrlZ);
    return () => {
      document.removeEventListener('keydown', onCtrlZ);
    };
  }, [undo, redo]);

  // const onNodesChange = useCallback(
  //   (changes: any) => {
  //     setNodes((nds) => applyNodeChanges(changes, nds));
  //   },
  //   [setNodes]
  // );
  // const onEdgesChange = useCallback(
  //   (changes: any) => {
  //     setEdges((eds) => applyEdgeChanges(changes, eds));
  //   },
  //   [setEdges]
  // );

  const onConnect = useCallback(
    (params: Connection) => {
      const sourceNode = nodes.find((node) => node.id === params.source);
      const targetNode = nodes.find((node) => node.id === params.target);
      if (!(sourceNode && targetNode)) return;
      setEdges((eds) => addEdge(params, eds));
    },
    [nodes, setEdges]
  );

  // TODO leave only one
  const onBeforeDelete = useCallback(
    async ({ nodes, edges }: { nodes: Node[]; edges: Edge[] }) => ({
      nodes: nodes.filter((node) => node.type !== 'IO' || !node.data.isOutput),
      edges,
    }),
    []
  );

  const inputNodes = useMemo<IONodeType[]>(
    () =>
      nodes.filter(
        (node) => node.type === 'IO' && !node.data.isOutput
      ) as IONodeType[],
    [nodes]
  );

  const processedNodes = useMemo<Node[]>(
    // TODO replace with n inputs
    // () =>
    //   nodes.map((node: Node) => {
    //     if (graphs && node.type === 'Subgraph') {
    //       const update: Record<string, any> = { graphs };
    //       const subgraphId = node.data.subgraphId;
    //       if (subgraphId) {
    //         const subgraph = graphs.find((graph) => graph.id === subgraphId);
    //         update.isMultiInput = subgraph && subgraph.isMultiInput;
    //         update.isMultiOutput = subgraph && subgraph.isMultiOutput;
    //       }
    //       return { ...node, data: { ...node.data, ...update } };
    //     }
    //     return node;
    //   }),
    () => {
      const updatedNodes: Node[] = [];
      let i = 0;
      for (const node of nodes) {
        if (graphs && node.type === 'Subgraph') {
          updatedNodes.push({
            ...node,
            data: {
              ...node.data,
              graphs,
            },
          });
          continue;
        }
        if (node.type === 'IO' && !node.data.isOutput) {
          const nodeData = node.data;
          updatedNodes.push({
            ...node,
            data: {
              ...nodeData,
              id: i,
            },
          });
          i += 1;
          continue;
        }
        updatedNodes.push(node);
      }
      return updatedNodes;
    },
    [nodes, graphs]
  );

  const processedEdges = useMemo<Edge[]>(
    () =>
      edges.map((edge: Edge) => {
        if (testOpen) {
          const edgeData = getEdgeData(executionOutputs, edge);
          if (edgeData && edgeData.length) {
            return {
              ...edge,
              className: 'processed-edge',
            };
          }
        }

        return edge;
      }),
    [edges, executionOutputs, testOpen]
  );

  const onPaneClick = useCallback(
    (event: MouseEvent) => {
      if (
        selectedTool === 'select' ||
        event.ctrlKey ||
        event.altKey ||
        event.shiftKey
      )
        return;

      event.preventDefault();

      const { x, y } = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const newNode = createNode(selectedTool, x, y, nodesDescriptions);
      if (newNode) {
        // TODO reindex inputs
        setNodes((nds: Node[]) => [...nds, newNode]);
      }
      setSelectedTool('select');
    },
    [selectedTool, setNodes, screenToFlowPosition, nodesDescriptions]
  );

  const onSelectTool = (toolType: ToolType) => {
    setSelectedTool(toolType);
  };

  const onEdgeClick = useCallback(
    (event: MouseEvent, edge: Edge) => {
      const edgeData = getEdgeData(executionOutputs, edge);
      setSelectedObjectData(edgeData || null);
    },
    [executionOutputs]
  );

  const onNodeClick = useCallback(
    (event: MouseEvent, node: Node) => {
      if (!lastExecutionResult?.llm_node_stats) {
        setSelectedObjectData(null);
        return;
      }
      const nodeStats = lastExecutionResult.llm_node_stats[node.id];
      if (nodeStats) {
        setSelectedObjectData([
          `Completion tokens: ${nodeStats.completion_tokens}\n` +
            `Prompt tokens: ${nodeStats.prompt_tokens}\n` +
            `Total tokens: ${nodeStats.total_tokens}\n` +
            (nodeStats.approximate_cost > 0
              ? `Approximate cost: $${nodeStats.approximate_cost.toFixed(6)}`
              : ''),
        ]);
      } else {
        setSelectedObjectData(null);
      }
    },
    [lastExecutionResult?.llm_node_stats]
  );

  const onExportClick = () => {
    if (!exportLinkRef.current) return;
    const exportData = {
      nodes,
      edges,
      id: graphId,
    };
    const exportText = JSON.stringify(exportData);
    const blob = new Blob([exportText], { type: 'application/json' });
    const fileName = `${projectName}-${new Date().toISOString()}.json`;
    const url = window.URL.createObjectURL(blob);
    try {
      exportLinkRef.current.href = url;
      exportLinkRef.current.download = fileName;
      exportLinkRef.current.click();
    } finally {
      window.URL.revokeObjectURL(url);
    }
  };

  const importFromJson = (jsonData: string) => {
    try {
      const graphData = JSON.parse(jsonData);
      const { nodes, edges } = graphData;
      if (!nodes || !edges) {
        messageApi.error('Import error');
        return;
      }
      setNodes(nodes);
      setEdges(edges);
      console.log(graphData);
    } catch (error) {
      messageApi.error('Import error');
      console.log('Import error:', error);
    }
  };

  const setExecutionResult = useCallback((result: ExecutionResult | null) => {
    setLastExecutionResult(result);
    if (!result?.results) {
      setExecutionOutputs(new Map());
      return;
    }
    const nodeOutputs = new Map(
      Object.entries(result.results).map(([nodeId, outputs]) => [
        nodeId,
        new Map(outputs.map(({ name, values }) => [name, values])),
      ])
    );
    setExecutionOutputs(nodeOutputs);
  }, []);

  if (loading) {
    return (
      <Flex align="center" justify="center" className="lpe-placeholder">
        {contextHolder}
        <Empty description="Loading" image={<Spin size="large" />} />
      </Flex>
    );
  }

  if (errorFetching) {
    return <ErrorPlaceholder>{contextHolder}</ErrorPlaceholder>;
  }

  return (
    <>
      {contextHolder}
      <ReactFlow
        nodes={processedNodes}
        edges={processedEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onBeforeDelete={onBeforeDelete}
        onEdgeClick={onEdgeClick}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        // onNodeContextMenu={changeNodeIOType}
        nodeTypes={nodeTypes}
        deleteKeyCode={['Delete', 'Backspace']}
        fitView
      >
        <Breadcrumb
          className="graph-editor-breadcrumb"
          items={[
            {
              title: <Link to="/graphs">Graphs</Link>,
            },
            {
              title: graphName,
            },
          ]}
        />
        <div className="graph-tools-container">
          <ToolsPanel
            selctedTool={selectedTool}
            onSelectTool={onSelectTool}
            nodesDescriptions={nodesDescriptions}
          />
        </div>
        <div className={testOpen ? 'graph-test-panel-container' : 'lpe-hidden'}>
          <TestPanel
            graphId={graphId}
            onClose={() => setTestOpen(false)}
            onExecutionResult={setExecutionResult}
            edgeData={selectedObjectData}
            inputNodes={inputNodes}
          />
        </div>
        <div className="graph-extra-tools">
          <Button
            size="large"
            icon={<CaretRightOutlined />}
            onClick={() => setTestOpen(true)}
          >
            Run
          </Button>
          <Button
            size="large"
            icon={<ImportOutlined />}
            onClick={() => setImportOpen(true)}
          >
            Import
          </Button>
          <Button
            size="large"
            icon={<ExportOutlined />}
            onClick={onExportClick}
          >
            Export
          </Button>
          <a style={{ display: 'none' }} ref={exportLinkRef} />
          <Tooltip title="ctrl+z">
            <Button size="large" icon={<UndoOutlined />} onClick={undo}>
              Undo
            </Button>
          </Tooltip>
          <Tooltip title="shift+ctrl+z">
            <Button size="large" icon={<RedoOutlined />} onClick={redo}>
              Redo
            </Button>
          </Tooltip>
        </div>
        {/* )} */}
      </ReactFlow>
      <Modal
        open={importOpen}
        onCancel={() => setImportOpen(false)}
        closeIcon={null}
        footer={null}
      >
        <Upload.Dragger
          className="import-dropzone"
          multiple={false}
          beforeUpload={async (file) => {
            importFromJson(await file.text());
            setImportOpen(false);
            return false;
          }}
          maxCount={1}
          fileList={[]}
        >
          <Typography.Title level={4}>
            <ImportOutlined /> Import
          </Typography.Title>
        </Upload.Dragger>
      </Modal>
    </>
  );
}

export default function GraphEditorContainer() {
  return (
    <ReactFlowProvider>
      <GraphEditor />
    </ReactFlowProvider>
  );
}
